<script setup>
// Auto-extracted chrome for Waermepumpensofort.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Wärmepumpe Sofort" },
    brandTagline: { type: String, default: "In 4 Wochen eingebaut. Rückruf in 15 Minuten." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Wärmepumpe Sofort");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "In 4 Wochen eingebaut. Rückruf in 15 Minuten.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="wps" :style="`--wps-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="wps-top">
                    <div class="wps-top__inner">
                        <div class="wps-top__brand">
                            <span class="wps-pulse" aria-hidden="true"></span>
                            <div>
                                <strong>{{ brandName }}</strong>
                                <span>{{ brandTagline }}</span>
                            </div>
                        </div>
                    </div>
                </header>
        <slot />
        <footer class="wps-foot">
                    <div class="wps-foot__inner">
                        <p>{{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="wps-foot__copy">&copy; {{ new Date().getFullYear() }} waermepumpensofort.de &middot; Express-Disposition ohne Gewähr für Geräte-Lieferfristen Dritter.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.wps {
    --wps-bg: #fafaf9;
    --wps-paper: #ffffff;
    --wps-ink: #1c1917;
    --wps-sub: #57534e;
    --wps-muted: #a8a29e;
    --wps-line: #e7e5e4;
    --wps-line-strong: #d6d3d1;
    --wps-accent: #DC2626;
    --wps-accent-deep: #991B1B;
    --wps-accent-soft: #FEE2E2;
    --wps-phone: #16a34a;
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--wps-ink);
    background: var(--wps-bg);
    min-height: 100dvh;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
}
.wps *, .wps *::before, .wps *::after { box-sizing: border-box; }
.wps h1, .wps h2, .wps h3, .wps h4 { font-family: 'Space Grotesk', system-ui, sans-serif; font-weight: 700; letter-spacing: -0.02em; line-height: 1.1; margin: 0; color: var(--wps-ink); }
.wps a { color: inherit; }

.wps-mark { color: var(--wps-accent); }

/* Top bar */
.wps-top { background: var(--wps-ink); color: #fff; }
.wps-top__inner { max-width: 1240px; margin: 0 auto; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.wps-top__brand { display: flex; align-items: center; gap: 12px; }
.wps-top__brand strong { display: block; font-family: 'Space Grotesk', sans-serif; font-size: 16px; line-height: 1.1; }
.wps-top__brand span { display: block; color: #d6d3d1; font-size: 12px; }
.wps-pulse { width: 10px; height: 10px; background: var(--wps-accent); border-radius: 50%; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); animation: wpsPulse 1.6s infinite; }
@keyframes wpsPulse { 0% { box-shadow: 0 0 0 0 rgba(220,38,38,0.6); } 70% { box-shadow: 0 0 0 12px rgba(220,38,38,0); } 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); } }
.wps-top__phone { display: flex; flex-direction: column; align-items: flex-end; text-decoration: none; padding: 6px 14px; border: 1px solid #44403c; border-radius: 4px; transition: border-color 200ms ease; }
.wps-top__phone:hover { border-color: var(--wps-accent); }
.wps-top__phoneLabel { font-size: 11px; color: #a8a29e; letter-spacing: 0.06em; text-transform: uppercase; }
.wps-top__phoneNum { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px; color: #fff; }

/* Hero */
.wps-hero { padding: 56px 20px 32px; }
.wps-hero__inner { max-width: 1240px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 36px; align-items: stretch; }
@media (min-width: 980px) { .wps-hero { padding: 88px 20px 56px; } .wps-hero__inner { grid-template-columns: 1.45fr 1fr; gap: 56px; } }
.wps-tag { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--wps-accent-deep); background: var(--wps-accent-soft); padding: 6px 10px; border-radius: 3px; margin-bottom: 18px; }
.wps-tag--solid { background: var(--wps-accent); color: #fff; }
.wps-tag--ghost { background: transparent; color: var(--wps-accent); padding: 0; }
.wps-hero h1 { font-size: clamp(36px, 6.4vw, 68px); text-wrap: balance; margin-bottom: 18px; }
.wps-hero__sub { color: var(--wps-sub); font-size: 17px; max-width: 56ch; margin-bottom: 26px; }
.wps-hero__sub strong { color: var(--wps-ink); }
.wps-hero__cta { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 28px; }
.wps-trust { list-style: none; padding: 18px 0 0; margin: 0; display: flex; gap: 28px; border-top: 1px solid var(--wps-line); flex-wrap: wrap; }
.wps-trust li { display: flex; flex-direction: column; gap: 2px; }
.wps-trust strong { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 22px; color: var(--wps-accent); }
.wps-trust span { font-size: 12px; color: var(--wps-sub); }

.wps-hero__card { background: var(--wps-paper); border: 1px solid var(--wps-line); border-left: 4px solid var(--wps-accent); padding: 28px 26px; border-radius: 4px; }
.wps-card__chip { font-size: 11px; font-weight: 700; letter-spacing: 0.14em; color: var(--wps-accent-deep); margin-bottom: 16px; }
.wps-card__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 18px; }
.wps-card__list li { display: grid; grid-template-columns: auto 1fr; gap: 14px; align-items: start; }
.wps-card__list span { font-family: 'Space Grotesk', sans-serif; font-weight: 700; color: var(--wps-muted); font-size: 14px; }
.wps-card__list strong { font-family: 'Space Grotesk', sans-serif; font-weight: 700; display: block; font-size: 15px; margin-bottom: 2px; }
.wps-card__list p { margin: 0; color: var(--wps-sub); font-size: 14px; line-height: 1.5; }
.wps-card__foot { font-size: 12px; color: var(--wps-muted); margin-top: 18px; padding-top: 14px; border-top: 1px dashed var(--wps-line-strong); }

/* Buttons */
.wps-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14px; padding: 12px 22px; border-radius: 4px; border: 2px solid transparent; cursor: pointer; transition: all 200ms ease; text-decoration: none; text-transform: uppercase; letter-spacing: 0.06em; }
.wps-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.wps-btn--cta { background: var(--wps-accent); color: #fff; border-color: var(--wps-accent); }
.wps-btn--cta:hover:not(:disabled) { background: var(--wps-accent-deep); border-color: var(--wps-accent-deep); transform: translateY(-1px); }
.wps-btn--phone { background: var(--wps-phone); color: #fff; border-color: var(--wps-phone); }
.wps-btn--phone:hover { background: #15803d; border-color: #15803d; transform: translateY(-1px); }
.wps-btn--ghost { background: transparent; color: var(--wps-sub); border-color: var(--wps-line-strong); }
.wps-btn--ghost:hover { color: var(--wps-ink); border-color: var(--wps-ink); }
.wps-btn--lg { padding: 15px 26px; font-size: 14px; }
.wps-btn--sm { padding: 9px 14px; font-size: 12px; }
.wps-btn--full { width: 100%; }

/* Express form */
.wps-express { background: var(--wps-paper); border-top: 1px solid var(--wps-line); border-bottom: 1px solid var(--wps-line); padding: 64px 20px; }
.wps-express__inner { max-width: 760px; margin: 0 auto; }
.wps-express__head { margin-bottom: 32px; }
.wps-express__head h2 { font-size: clamp(28px, 4.4vw, 44px); margin-bottom: 10px; }
.wps-express__kicker { color: var(--wps-sub); font-size: 15px; }
.wps-prog { margin-top: 22px; }
.wps-prog__bar { height: 6px; background: var(--wps-line); border-radius: 999px; overflow: hidden; }
.wps-prog__bar span { display: block; height: 100%; background: var(--wps-accent); transition: width 320ms cubic-bezier(0.32, 0.72, 0, 1); }
.wps-prog__lbl { font-size: 12px; color: var(--wps-sub); margin-top: 8px; letter-spacing: 0.04em; }

.wps-step { background: var(--wps-bg); border: 1px solid var(--wps-line); border-radius: 6px; padding: 28px 24px; }
.wps-fld { display: grid; gap: 14px; }
.wps-fld__help { color: var(--wps-muted); font-size: 13px; }
.wps-fld__read { text-align: center; padding: 6px 0; }
.wps-fld__read strong { display: block; font-family: 'Space Grotesk', sans-serif; font-size: 44px; color: var(--wps-accent); line-height: 1; }
.wps-fld__read span { color: var(--wps-sub); font-size: 13px; }

.wps-input { width: 100%; padding: 14px 16px; font-size: 16px; border: 1px solid var(--wps-line-strong); border-radius: 4px; background: #fff; font-family: inherit; color: var(--wps-ink); }
.wps-input:focus { outline: none; border-color: var(--wps-accent); box-shadow: 0 0 0 3px var(--wps-accent-soft); }
.wps-input--big { font-family: 'Space Grotesk', sans-serif; font-size: 28px; text-align: center; letter-spacing: 0.16em; font-weight: 700; }

.wps-range { width: 100%; accent-color: var(--wps-accent); }

.wps-pick { display: grid; gap: 10px; }
.wps-pick__item { position: relative; text-align: left; padding: 16px 18px; background: #fff; border: 1px solid var(--wps-line-strong); border-radius: 4px; cursor: pointer; font-family: inherit; transition: all 160ms ease; display: flex; flex-direction: column; gap: 4px; }
.wps-pick__item:hover { border-color: var(--wps-accent); }
.wps-pick__item--on { border-color: var(--wps-accent); background: var(--wps-accent-soft); }
.wps-pick__item strong { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px; color: var(--wps-ink); }
.wps-pick__item span { color: var(--wps-sub); font-size: 13px; }
.wps-pick__flag { position: absolute; top: 12px; right: 14px; font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700; letter-spacing: 0.14em; color: #fff; background: var(--wps-accent); padding: 3px 8px; border-radius: 3px; }

.wps-nav { display: flex; justify-content: space-between; gap: 12px; margin-top: 24px; }

/* Result */
.wps-result { display: grid; gap: 28px; }
.wps-score { background: var(--wps-paper); border: 1px solid var(--wps-line); border-radius: 6px; padding: 28px; display: grid; grid-template-columns: auto 1fr; gap: 22px; align-items: center; }
@media (max-width: 540px) { .wps-score { grid-template-columns: 1fr; text-align: center; } }
.wps-score__num { font-family: 'Space Grotesk', sans-serif; padding: 16px 22px; border-radius: 6px; min-width: 140px; text-align: center; background: var(--wps-bg); border: 2px solid var(--wps-line-strong); }
.wps-score__num strong { display: block; font-size: 64px; line-height: 1; font-weight: 700; color: var(--wps-accent); }
.wps-score__num span { display: block; color: var(--wps-sub); font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
.wps-score__num--hot { border-color: var(--wps-accent); background: var(--wps-accent-soft); }
.wps-score__num--warm { border-color: #f59e0b; background: #fef3c7; }
.wps-score__num--warm strong { color: #b45309; }
.wps-score__num--lukewarm { border-color: #6b7280; background: #f3f4f6; }
.wps-score__num--lukewarm strong { color: #4b5563; }
.wps-score__num--cold { border-color: var(--wps-line-strong); }
.wps-score__num--cold strong { color: var(--wps-sub); }

.wps-score__band { font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 0.12em; padding: 6px 10px; border-radius: 3px; display: inline-block; margin-bottom: 10px; }
.wps-score__band--hot { background: var(--wps-accent); color: #fff; }
.wps-score__band--warm { background: #f59e0b; color: #fff; }
.wps-score__band--lukewarm { background: #6b7280; color: #fff; }
.wps-score__band--cold { background: var(--wps-line-strong); color: var(--wps-ink); }
.wps-score__sla { color: var(--wps-sub); font-size: 14px; }
.wps-score__sla strong { color: var(--wps-ink); }

/* Lead form */
.wps-lead { background: var(--wps-paper); border: 1px solid var(--wps-line); border-radius: 6px; padding: 28px 26px; }
.wps-lead h3 { font-size: 22px; margin-bottom: 6px; }
.wps-lead > p { color: var(--wps-sub); font-size: 14px; margin-bottom: 18px; }
.wps-form { display: grid; gap: 14px; }
.wps-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 540px) { .wps-form__row { grid-template-columns: 1fr; } }
.wps-fl { display: flex; flex-direction: column; gap: 6px; }
.wps-fl > span { font-size: 13px; font-weight: 600; color: var(--wps-ink); }
.wps-fl em { font-weight: 400; color: var(--wps-muted); font-style: normal; font-size: 12px; }
.wps-fl input { padding: 12px 14px; font-size: 15px; border: 1px solid var(--wps-line-strong); border-radius: 4px; background: #fff; color: var(--wps-ink); font-family: inherit; }
.wps-fl input:focus { outline: none; border-color: var(--wps-accent); box-shadow: 0 0 0 3px var(--wps-accent-soft); }
.wps-fl--err input { border-color: var(--wps-accent); background: var(--wps-accent-soft); }
.wps-consent { display: flex; gap: 12px; padding: 14px; background: var(--wps-bg); border: 1px solid var(--wps-line); border-radius: 4px; font-size: 12px; color: var(--wps-sub); line-height: 1.55; cursor: pointer; }
.wps-consent input { margin-top: 2px; flex-shrink: 0; accent-color: var(--wps-accent); }
.wps-consent--err { border-color: var(--wps-accent); }
.wps-err { color: var(--wps-accent); font-size: 13px; text-align: center; font-weight: 600; }
.wps-leg { color: var(--wps-muted); font-size: 12px; text-align: center; }

.wps-done { text-align: center; padding: 32px 24px; background: var(--wps-paper); border: 1px solid var(--wps-line); border-radius: 6px; }
.wps-done__chip { display: inline-block; background: var(--wps-accent); color: #fff; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 11px; letter-spacing: 0.14em; padding: 6px 12px; border-radius: 3px; margin-bottom: 16px; }
.wps-done h3 { font-size: 26px; margin-bottom: 10px; }
.wps-done p { color: var(--wps-sub); margin-bottom: 18px; }

/* How */
.wps-how { padding: 80px 20px; background: var(--wps-bg); }
.wps-how__inner { max-width: 1120px; margin: 0 auto; }
.wps-how h2 { font-size: clamp(28px, 4.2vw, 44px); margin-bottom: 40px; max-width: 22ch; }
.wps-how__list { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr; gap: 28px; }
@media (min-width: 720px) { .wps-how__list { grid-template-columns: repeat(2, 1fr); gap: 36px 44px; } }
@media (min-width: 1040px) { .wps-how__list { grid-template-columns: repeat(4, 1fr); } }
.wps-how__list li { padding-top: 18px; border-top: 2px solid var(--wps-accent); }
.wps-how__no { font-family: 'Space Grotesk', sans-serif; font-weight: 700; color: var(--wps-accent); font-size: 13px; letter-spacing: 0.1em; margin-bottom: 8px; }
.wps-how__list h4 { font-size: 17px; margin-bottom: 8px; }
.wps-how__list p { color: var(--wps-sub); font-size: 14px; line-height: 1.55; }

/* FAQ */
.wps-faq { padding: 72px 20px; background: var(--wps-paper); border-top: 1px solid var(--wps-line); }
.wps-faq__inner { max-width: 880px; margin: 0 auto; }
.wps-faq h2 { font-size: clamp(26px, 4vw, 38px); margin-bottom: 30px; max-width: 28ch; }
.wps-faq__grid { display: grid; gap: 0; border-top: 1px solid var(--wps-line); }
.wps-faq__grid details { border-bottom: 1px solid var(--wps-line); padding: 18px 0; }
.wps-faq__grid summary { cursor: pointer; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 16px; color: var(--wps-ink); list-style: none; display: flex; justify-content: space-between; gap: 16px; }
.wps-faq__grid summary::-webkit-details-marker { display: none; }
.wps-faq__grid summary::after { content: '+'; color: var(--wps-accent); font-size: 22px; font-weight: 400; transition: transform 200ms ease; }
.wps-faq__grid details[open] summary::after { content: '\2013'; }
.wps-faq__grid p { color: var(--wps-sub); font-size: 14px; margin: 12px 0 0; max-width: 64ch; line-height: 1.6; }

/* Author */
.wps-author { padding: 32px 20px; background: var(--wps-bg); border-top: 1px solid var(--wps-line); }
.wps-author__inner { max-width: 1120px; margin: 0 auto; }
.wps-author__line { color: var(--wps-sub); font-size: 14px; }

/* Footer */
.wps-foot { background: var(--wps-ink); color: #d6d3d1; padding: 32px 20px 88px; }
.wps-foot__inner { max-width: 1240px; margin: 0 auto; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; }
.wps-foot nav { display: flex; gap: 18px; }
.wps-foot a { color: #d6d3d1; text-decoration: none; }
.wps-foot a:hover { color: var(--wps-accent); }
.wps-foot__copy { font-size: 12px; color: #a8a29e; flex-basis: 100%; padding-top: 12px; border-top: 1px solid #44403c; }

/* Sticky CTA (mobile) */
.wps-stick { position: fixed; left: 0; right: 0; bottom: 0; padding-bottom: env(safe-area-inset-bottom); background: rgba(28, 25, 23, 0.97); backdrop-filter: blur(8px); border-top: 2px solid var(--wps-accent); z-index: 40; }
.wps-stick__inner { max-width: 1240px; margin: 0 auto; padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.wps-stick__phone { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14px; color: #fff; text-decoration: none; }
@media (min-width: 980px) { .wps-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .wps *, .wps *::before, .wps *::after { transition: none !important; animation: none !important; transform: none !important; }
}

/* ── Trust strip (images + cites + byline) — wps-* unique treatment ── */
.wps-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.wps-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .wps-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.wps-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 20px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 2px solid #DC2626; clip-path: polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px)); box-shadow: 0 8px 24px -14px #DC262666;
}
.wps-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.wps-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.wps-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.wps-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.wps-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.wps-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #DC262655;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.wps-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.wps-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #DC2626;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.wps-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.wps-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.wps-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.wps-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.wps-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.wps-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
